import { useCallback, useMemo, useState, type PropsWithChildren } from 'react';
import { SettingsHubContext, type SettingsHubContextValue } from './SettingsHubContext';
import { SettingsHubModal } from './SettingsHubModal';

/**
 * Holds the hub's open state so any page can open it via `useSettingsHub()`. Mounted only while
 * open, so it resets on each open and focus returns to the trigger on close.
 */
export const SettingsHubProvider = ({ children }: PropsWithChildren) => {
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openSettings = useCallback((groupId?: string) => {
    setOpenGroupId(groupId ?? null);
    setIsOpen(true);
  }, []);

  const closeSettings = useCallback(() => setIsOpen(false), []);

  const value = useMemo<SettingsHubContextValue>(
    () => ({ isOpen, openSettings, closeSettings }),
    [isOpen, openSettings, closeSettings],
  );

  return (
    <SettingsHubContext.Provider value={value}>
      {children}
      {isOpen && <SettingsHubModal initialGroupId={openGroupId ?? undefined} onHide={closeSettings} />}
    </SettingsHubContext.Provider>
  );
};
